import React, { useMemo, useState } from "react";

// @material-ui/core
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Checkbox from "@material-ui/core/Checkbox";
import ListItemText from "@material-ui/core/ListItemText";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import GetAppIcon from "@material-ui/icons/GetApp";

// core components
import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer";

import {
  Map,
  CircleMarker,
  Tooltip,
  //@ts-ignore
} from "react-leaflet";
import GoogleLayer from "components/Map/GoogleLayer.js";
import SpinLoading from "components/Loading/SpinLoading.js";

import "react-bootstrap-range-slider/dist/react-bootstrap-range-slider.css";
import intl from "react-intl-universal";

import { useRootSelector } from "hooks/reduxHooks";
import Reading from "spectral-core/models/Reading";
import { useUserLoggedIn } from "hooks/userHooks";
import { useGetReadings } from "hooks/readingsHooks";
import { parsePredictedValues } from "utils/nutroScanAggregates";
import { applicationLabel } from "utils/dashboardAggregates";
import { exportToExcel } from "utils/excelExport";

const useStyles = makeStyles({
  container: { padding: 16 },
  title: { fontSize: 20, fontWeight: 600, color: "#37474f", marginBottom: 12 },
  filters: {
    padding: "12px 16px",
    marginBottom: 12,
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    alignItems: "flex-end",
  },
});

const CHART_BAR_COLORS = [
  "#00838f", "#26a69a", "#43a047", "#66bb6a",
  "#8bc34a", "#cddc39", "#ffb300", "#fb8c00",
  "#ef5350", "#ab47bc", "#5c6bc0", "#29b6f6",
];

type ColorByOption = "usuario" | "aplicacao";

function userLabel(r: Reading): string {
  return (r.userName || "").trim() || "—";
}

function getColorByKey(r: Reading, colorBy: ColorByOption): string {
  switch (colorBy) {
    case "usuario": return userLabel(r);
    case "aplicacao": return applicationLabel(r);
  }
}

interface Pin {
  position: [number, number];
  name: string;
  usuario: string;
  aplicacao: string;
  detail: string;
  color: string;
  groupKey: string;
}

export default function Coordinates() {
  const classes = useStyles();

  const isLoggedIn = useUserLoggedIn();
  useGetReadings();

  const rawReadings = useRootSelector((state) => state.reading.readings) as
    | Reading[]
    | undefined;

  const loading = !isLoggedIn || rawReadings === undefined;

  const [filterUser, setFilterUser] = useState<string[]>([]);
  const [filterApplication, setFilterApplication] = useState<string>("");
  const [filterDateFrom, setFilterDateFrom] = useState<string>("");
  const [filterDateTo, setFilterDateTo] = useState<string>("");
  const [colorBy, setColorBy] = useState<ColorByOption>("usuario");

  const baseReadings = useMemo(() => rawReadings ?? [], [rawReadings]);

  const availableUsers = useMemo(() => {
    const set = new Set<string>();
    for (const r of baseReadings) {
      const u = (r.userName || "").trim();
      if (u) set.add(u);
    }
    return Array.from(set).sort();
  }, [baseReadings]);

  const afterUser = useMemo(
    () =>
      filterUser.length > 0
        ? baseReadings.filter((r) => filterUser.includes((r.userName || "").trim()))
        : baseReadings,
    [baseReadings, filterUser]
  );

  const availableApplications = useMemo(() => {
    const set = new Set<string>();
    for (const r of afterUser) set.add(applicationLabel(r));
    return Array.from(set).sort();
  }, [afterUser]);

  // Aplica todos os filtros
  const filteredReadings = useMemo(() => {
    let f = afterUser;
    if (filterApplication) {
      f = f.filter((r) => applicationLabel(r) === filterApplication);
    }
    if (filterDateFrom) {
      const from = new Date(filterDateFrom + "T00:00:00");
      f = f.filter((r) => {
        const dt = r.dateTime;
        if (!dt) return false;
        const [d, m, y] = dt.split(" ")[0].split("/").map(Number);
        return new Date(y, m - 1, d) >= from;
      });
    }
    if (filterDateTo) {
      const to = new Date(filterDateTo + "T23:59:59");
      f = f.filter((r) => {
        const dt = r.dateTime;
        if (!dt) return false;
        const [d, m, y] = dt.split(" ")[0].split("/").map(Number);
        return new Date(y, m - 1, d) <= to;
      });
    }
    return f;
  }, [afterUser, filterApplication, filterDateFrom, filterDateTo]);

  // Mapa de cores por dimensão selecionada
  const colorMap = useMemo(() => {
    const map: Record<string, string> = {};
    let idx = 0;
    for (const r of filteredReadings) {
      const key = getColorByKey(r, colorBy);
      if (!map[key]) {
        map[key] = CHART_BAR_COLORS[idx % CHART_BAR_COLORS.length];
        idx++;
      }
    }
    return map;
  }, [filteredReadings, colorBy]);

  // Contagem de leituras por classe (incluindo as sem coordenada)
  const classCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const r of filteredReadings) {
      const key = getColorByKey(r, colorBy);
      counts[key] = (counts[key] || 0) + 1;
    }
    return counts;
  }, [filteredReadings, colorBy]);

  // Legenda ordenada por contagem desc
  const legendEntries = useMemo(() => {
    return Object.entries(classCounts)
      .sort((a, b) => b[1] - a[1])
      .map(([name, count]) => ({
        name,
        count,
        color: colorMap[name] || "#9e9e9e",
      }));
  }, [classCounts, colorMap]);

  const pins: Pin[] = useMemo(() => {
    const out: Pin[] = [];
    for (const r of filteredReadings) {
      try {
        if (!r.geoLocation || r.geoLocation.length < 10) continue;
        const { Latitude, Longitude } = JSON.parse(r.geoLocation);
        const groupKey = getColorByKey(r, colorBy);
        const preds = parsePredictedValues(r);
        const detail = preds
          .slice(0, 5)
          .map((p) => `${p.ParameterName}: ${p.Value?.toFixed?.(2) ?? p.Value}`)
          .join(", ");
        out.push({
          position: [Latitude, Longitude],
          name: r.name || "—",
          usuario: userLabel(r),
          aplicacao: applicationLabel(r),
          detail,
          groupKey,
          color: colorMap[groupKey] || "#9e9e9e",
        });
      } catch {
        /* skip */
      }
    }
    return out;
  }, [filteredReadings, colorMap, colorBy]);

  if (loading) {
    return <SpinLoading />;
  }

  return (
    <div>
      <GridContainer>
        {/*@ts-ignore*/}
        <GridItem xs={12}>
          <Paper className={classes.container}>
            <Typography className={classes.title}>
              {intl.get("coordinate.coordinate")}
            </Typography>

            {/* Filtros */}
            <Paper className={classes.filters} elevation={0}>
              <FormControl style={{ minWidth: 160, flex: "1 1 160px" }} size="small">
                <InputLabel id="coord-filter-user" shrink>Usuário</InputLabel>
                <Select
                  labelId="coord-filter-user"
                  multiple
                  value={filterUser}
                  onChange={(e: React.ChangeEvent<{ value: unknown }>) => {
                    setFilterUser(e.target.value as string[]);
                    setFilterApplication("");
                  }}
                  displayEmpty
                  renderValue={(selected) => {
                    const sel = selected as string[];
                    if (sel.length === 0) return "Todos";
                    if (sel.length === 1) return sel[0];
                    return `${sel.length} usuários`;
                  }}
                >
                  {availableUsers.map((u) => (
                    <MenuItem key={u} value={u}>
                      <Checkbox checked={filterUser.indexOf(u) > -1} size="small" color="primary" />
                      <ListItemText primary={u} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl style={{ minWidth: 160, flex: "1 1 160px" }} size="small">
                <InputLabel id="coord-filter-application" shrink>Aplicação</InputLabel>
                <Select
                  labelId="coord-filter-application"
                  value={filterApplication}
                  onChange={(e: React.ChangeEvent<{ value: unknown }>) =>
                    setFilterApplication(e.target.value as string)
                  }
                  displayEmpty
                >
                  <MenuItem value="">Todas</MenuItem>
                  {availableApplications.map((a) => (
                    <MenuItem key={a} value={a}>{a}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="De"
                type="date"
                size="small"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                style={{ width: 145, flex: "0 0 145px" }}
              />
              <TextField
                label="Até"
                type="date"
                size="small"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                style={{ width: 145, flex: "0 0 145px" }}
              />
              <Button
                variant="outlined"
                color="primary"
                startIcon={<GetAppIcon />}
                style={{ marginLeft: "auto" }}
                onClick={() =>
                  exportToExcel(
                    pins.map((p) => ({
                      nome: p.name,
                      usuario: p.usuario,
                      aplicacao: p.aplicacao,
                      latitude: p.position[0],
                      longitude: p.position[1],
                      detalhe: p.detail,
                    })),
                    "Coordenadas",
                    "Coordenadas",
                    [
                      { header: "Nome", field: "nome" },
                      { header: "Usuário", field: "usuario" },
                      { header: "Aplicação", field: "aplicacao" },
                      { header: "Latitude", field: "latitude" },
                      { header: "Longitude", field: "longitude" },
                      { header: "Parâmetros", field: "detalhe" },
                    ]
                  )
                }
              >
                Exportar Excel
              </Button>
            </Paper>

            <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 8 }}>
              <FormControl size="small" style={{ minWidth: 140 }}>
                <InputLabel id="coord-color-by">Legenda</InputLabel>
                <Select
                  labelId="coord-color-by"
                  value={colorBy}
                  onChange={(e: React.ChangeEvent<{ value: unknown }>) =>
                    setColorBy(e.target.value as ColorByOption)
                  }
                >
                  <MenuItem value="usuario">Usuário</MenuItem>
                  <MenuItem value="aplicacao">Aplicação</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="caption" color="textSecondary">
                {pins.length} ponto(s) com coordenadas — {filteredReadings.length} leitura(s) total
              </Typography>
            </div>

            {/* Mapa + Legenda lado a lado */}
            <div style={{ display: "flex", gap: 16, marginTop: 8 }}>
              {/* Mapa */}
              <div style={{ flex: 1, height: "65vh", minWidth: 0 }}>
                {pins.length ? (
                  <Map
                    center={pins[0].position as [number, number]}
                    zoom={5}
                    style={{ height: "100%", borderRadius: 4 }}
                  >
                    <GoogleLayer />
                    {pins.map((pin, i) => (
                      <CircleMarker
                        key={i}
                        center={pin.position}
                        radius={6}
                        fillOpacity={0.7}
                        color={pin.color}
                        fillColor={pin.color}
                      >
                        <Tooltip direction="top" offset={[-8, -2]} opacity={1}>
                          <span>{pin.name}</span>
                          <br />
                          <span>{pin.usuario} — {pin.aplicacao}</span>
                          {pin.detail && (
                            <>
                              <br />
                              <span>{pin.detail}</span>
                            </>
                          )}
                        </Tooltip>
                      </CircleMarker>
                    ))}
                  </Map>
                ) : (
                  <Typography variant="body2" color="textSecondary" style={{ textAlign: "center", padding: 40 }}>
                    Sem coordenadas nas leituras carregadas.
                  </Typography>
                )}
              </div>

              {/* Legenda à direita */}
              {legendEntries.length > 0 && (
                <div style={{
                  width: 220,
                  flexShrink: 0,
                  maxHeight: "65vh",
                  overflowY: "auto",
                  paddingRight: 4,
                }}>
                  <Typography variant="subtitle2" style={{ marginBottom: 8, fontWeight: 600, color: "#37474f" }}>
                    {colorBy === "usuario" ? "Usuário" : "Aplicação"}
                  </Typography>
                  {legendEntries.map((entry) => (
                    <div
                      key={entry.name}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        marginBottom: 6,
                        fontSize: 13,
                        lineHeight: 1.3,
                      }}
                    >
                      <span style={{
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        backgroundColor: entry.color,
                        display: "inline-block",
                        flexShrink: 0,
                      }} />
                      <span style={{ fontWeight: 500 }}>{entry.name}</span>
                      <span style={{ color: "#666", whiteSpace: "nowrap" }}>({entry.count})</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Paper>
        </GridItem>
      </GridContainer>
    </div>
  );
}
